/**
 * House of Aalaya — product gallery and lightbox.
 *
 * <media-gallery>: thumbnail switching, and opening the lightbox at the
 * current image when the main image is clicked.
 *
 * <product-lightbox>: full-screen viewer with arrows, thumbnail strip,
 * counter, zoom toggle, drag to pan when zoomed and swipe between images.
 * Opening, closing, focus trapping and Escape come from window.aalayaOverlay.
 */
(function () {
  'use strict';

  var ZOOM_SCALE = 2.5;
  var SWIPE_THRESHOLD = 50;
  var DRAG_THRESHOLD = 6;

  function scrollIntoList(item) {
    var list = item && item.parentElement;
    if (!list) return;

    list.scrollTo({
      top: item.offsetTop - (list.clientHeight - item.offsetHeight) / 2,
      left: item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2,
      behavior: 'smooth'
    });
  }

  /* ---------------------------------------------------------------- *
   * Gallery
   * ---------------------------------------------------------------- */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      var thumb = event.target.closest('[data-media-id]');
      if (thumb) {
        event.preventDefault();
        this.select(thumb);
        return;
      }

      if (event.target.closest('[data-gallery-zoom]')) {
        event.preventDefault();
        this.openLightbox();
      }
    }

    select(thumb) {
      this.querySelectorAll('[data-media-id]').forEach(function (node) {
        node.setAttribute('aria-current', node === thumb ? 'true' : 'false');
      });
      scrollIntoList(thumb);

      if (!this.main) return;
      this.main.dataset.currentMedia = thumb.dataset.mediaId;

      var image = this.main.querySelector('img');
      var source = thumb.querySelector('img');
      if (!image || !source) return;

      image.src = source.dataset.fullSrc || source.src;
      image.srcset = '';
      image.alt = source.alt;
    }

    openLightbox() {
      var lightbox = document.getElementById(this.dataset.lightbox);
      if (lightbox && typeof lightbox.openAt === 'function') {
        lightbox.openAt(this.main ? this.main.dataset.currentMedia : null);
      }
    }
  }

  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* ---------------------------------------------------------------- *
   * Lightbox
   * ---------------------------------------------------------------- */

  class ProductLightbox extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-lightbox-media]');
      try {
        this.media = data ? JSON.parse(data.textContent) : [];
      } catch (error) {
        this.media = [];
      }

      this.index = 0;
      this.scale = 1;
      this.x = 0;
      this.y = 0;
      this.drag = null;
      this.moved = false;

      this.stage = this.querySelector('[data-lightbox-stage]');
      this.image = this.querySelector('[data-lightbox-image]');
      this.counter = this.querySelector('[data-lightbox-counter]');
      this.zoomButton = this.querySelector('[data-lightbox-zoom]');
      this.zoomLabel = this.querySelector('[data-lightbox-zoom-label]');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-lightbox-thumb]'));

      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('aalaya:overlay:closed', this.resetZoom.bind(this));
      if (this.stage) this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this));

      if (this.image) {
        var loaded = function () {
          this.classList.remove('is-loading');
        }.bind(this);
        this.image.addEventListener('load', loaded);
        this.image.addEventListener('error', loaded);
      }

      window.addEventListener('resize', this.resetZoom.bind(this));
    }

    get isOpen() {
      return this.dataset.open === 'true';
    }

    openAt(mediaId) {
      var index = this.media.findIndex(function (item) {
        return String(item.id) === String(mediaId);
      });

      this.show(index < 0 ? 0 : index);
      if (window.aalayaOverlay) window.aalayaOverlay.open(this, '[data-lightbox-close]');
    }

    show(index) {
      var total = this.media.length;
      if (!total || !this.image) return;

      this.index = (index + total) % total;
      var item = this.media[this.index];

      this.resetZoom();

      // The <img> ships lazy so the page doesn't fetch it up front; once the
      // lightbox is in use it must load straight away.
      this.image.loading = 'eager';
      if (this.image.getAttribute('src') !== item.src) {
        this.classList.add('is-loading');
        this.image.src = item.src;
      }
      if (this.image.complete && this.image.naturalWidth) this.classList.remove('is-loading');

      this.image.alt = item.alt || '';
      if (item.width && item.height) {
        this.image.width = item.width;
        this.image.height = item.height;
      }

      if (this.counter) this.counter.textContent = this.index + 1 + ' / ' + total;

      var active = null;
      this.thumbs.forEach(function (thumb, i) {
        var current = i === this.index;
        thumb.setAttribute('aria-current', current ? 'true' : 'false');
        if (current) active = thumb;
      }, this);
      scrollIntoList(active);

      // Warm the neighbours so arrows feel instant.
      [this.index - 1, this.index + 1].forEach(function (i) {
        var neighbour = this.media[(i + total) % total];
        if (neighbour) new Image().src = neighbour.src;
      }, this);
    }

    onClick(event) {
      if (event.target.closest('[data-lightbox-prev]')) {
        this.show(this.index - 1);
        return;
      }

      if (event.target.closest('[data-lightbox-next]')) {
        this.show(this.index + 1);
        return;
      }

      var thumb = event.target.closest('[data-lightbox-thumb]');
      if (thumb) {
        this.show(Number(thumb.dataset.lightboxThumb));
        return;
      }

      if (event.target.closest('[data-lightbox-zoom]')) {
        this.toggleZoom();
        return;
      }

      if (event.target === this.image) {
        // A drag or swipe also ends in a click; only a still tap toggles zoom.
        if (this.moved) {
          this.moved = false;
          return;
        }
        this.toggleZoom(event);
        return;
      }

      if (event.target === this.stage && this.scale === 1 && !this.moved && window.aalayaOverlay) {
        window.aalayaOverlay.close(this);
      }
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'ArrowLeft') this.show(this.index - 1);
      else if (event.key === 'ArrowRight') this.show(this.index + 1);
    }

    toggleZoom(event) {
      if (this.scale > 1) {
        this.resetZoom();
        return;
      }

      this.scale = ZOOM_SCALE;
      this.x = 0;
      this.y = 0;

      // Keep the clicked point under the cursor.
      if (event && typeof event.clientX === 'number') {
        var rect = this.image.getBoundingClientRect();
        this.x = (rect.left + rect.width / 2 - event.clientX) * (this.scale - 1);
        this.y = (rect.top + rect.height / 2 - event.clientY) * (this.scale - 1);
      }

      this.clamp();
      this.apply();
    }

    resetZoom() {
      this.scale = 1;
      this.x = 0;
      this.y = 0;
      this.apply();
    }

    clamp() {
      if (!this.stage || !this.image) return;

      var maxX = Math.max(0, (this.image.offsetWidth * this.scale - this.stage.clientWidth) / 2);
      var maxY = Math.max(0, (this.image.offsetHeight * this.scale - this.stage.clientHeight) / 2);

      this.x = Math.min(maxX, Math.max(-maxX, this.x));
      this.y = Math.min(maxY, Math.max(-maxY, this.y));
    }

    apply() {
      if (!this.image) return;

      var zoomed = this.scale > 1;
      this.image.style.transform = zoomed
        ? 'translate3d(' + this.x + 'px, ' + this.y + 'px, 0) scale(' + this.scale + ')'
        : '';
      this.classList.toggle('is-zoomed', zoomed);

      if (this.zoomButton) this.zoomButton.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
      if (this.zoomLabel) {
        this.zoomLabel.textContent = zoomed ? this.zoomLabel.dataset.labelOut : this.zoomLabel.dataset.labelIn;
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('button')) return;

      this.moved = false;
      this.drag = {
        startX: event.clientX,
        startY: event.clientY,
        x: this.x,
        y: this.y
      };

      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.drag) return;

      var dx = event.clientX - this.drag.startX;
      var dy = event.clientY - this.drag.startY;
      if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) this.moved = true;

      if (this.scale > 1 && this.moved) {
        this.classList.add('is-dragging');
        this.x = this.drag.x + dx;
        this.y = this.drag.y + dy;
        this.clamp();
        this.apply();
      }
    }

    onPointerUp(event) {
      if (!this.drag) return;

      var dx = event.clientX - this.drag.startX;
      var dy = event.clientY - this.drag.startY;

      if (this.scale === 1 && Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        this.show(this.index + (dx < 0 ? 1 : -1));
      }

      this.drag = null;
      this.classList.remove('is-dragging');
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }
  }

  if (!customElements.get('product-lightbox')) customElements.define('product-lightbox', ProductLightbox);
})();
